import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUpdateMailAccount,
  useListMailLearnedRules,
  useCreateMailLearnedRule,
  useDeleteMailLearnedRule,
  useListMailOldThreadCandidates,
  useResolveMailOldThreadCandidates,
  usePromoteMailMessageToProject,
  getListMailLearnedRulesQueryKey,
  getListMailOldThreadCandidatesQueryKey,
  getListMailAccountsQueryKey,
  getGetMailOldThreadCandidatesSummaryQueryKey,
  getListProjectsQueryKey,
  type MailAccount,
  type MailLearnedRule,
  type MailMessage,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Loader2, Sparkles, Pause, Play } from "lucide-react";
import {
  TriageBadge,
  triageLabel,
  TRIAGE_CATEGORIES,
} from "@/components/triage-badge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export default function MailTriageSettings({ account }: { account: MailAccount }) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [oldThreadOpen, setOldThreadOpen] = useState(false);

  return (
    <div className="space-y-4 border-t border-border pt-3">
      <TriageProgress account={account} />
      <LearnedRulesEditor accountId={account.id} />

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <p className="text-xs text-muted-foreground min-w-0">
          Rattrapage des conversations anciennes pertinentes.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOldThreadOpen(true)}
          data-testid={`button-old-threads-${account.id}`}
        >
          Revoir les anciens fils
        </Button>
      </div>

      <OldThreadCandidatesDialog
        accountId={account.id}
        open={oldThreadOpen}
        onOpenChange={setOldThreadOpen}
      />
    </div>
  );
}

function DailyAiUsageDisplay({ account }: { account: MailAccount }) {
  const used = account.dailyAiUsageToday ?? 0;
  const cap = account.dailyAiAnalysisCap;
  const remaining = account.dailyAiUsageRemaining ?? Math.max(0, cap - used);
  const capReached = remaining <= 0;
  // When cap is 0, every call is "over the cap" — show a full bar so the
  // visual matches the textual "Plafond atteint" state (avoid an empty bar).
  const ratio = cap > 0 ? Math.min(1, used / cap) : capReached ? 1 : 0;
  const nearCap = !capReached && ratio >= 0.8;

  const barColor = capReached
    ? "bg-amber-500"
    : nearCap
      ? "bg-amber-400"
      : "bg-primary";
  const textColor = capReached
    ? "text-amber-700 dark:text-amber-400"
    : nearCap
      ? "text-amber-700 dark:text-amber-400"
      : "text-foreground";

  return (
    <div
      className="space-y-1.5"
      data-testid={`ai-usage-${account.id}`}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs font-medium truncate">Utilisation IA aujourd&apos;hui</span>
        </div>
        <span
          className={`text-xs font-mono shrink-0 whitespace-nowrap ${textColor}`}
          data-testid={`ai-usage-count-${account.id}`}
        >
          {used.toLocaleString("fr-FR")} / {cap.toLocaleString("fr-FR")}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      {capReached ? (
        <p className="text-[11px] text-amber-700 dark:text-amber-400">
          Plafond atteint. Les nouveaux mails sont marqués «&nbsp;à analyser plus tard&nbsp;»
          jusqu&apos;à minuit (UTC). Augmente le plafond ci-dessus si besoin.
        </p>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          Compteur partagé entre tous tes comptes mail. Remis à zéro chaque jour à minuit (UTC).
        </p>
      )}
    </div>
  );
}

function formatBacklogEta(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 90) return "moins d'une minute";
  const min = Math.round(seconds / 60);
  if (min < 60) return `~${min} min`;
  const h = Math.floor(min / 60);
  const rm = min % 60;
  if (h < 24) return rm ? `~${h} h ${rm} min` : `~${h} h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `~${d} j ${rh} h` : `~${d} j`;
}

function TriageProgress({ account }: { account: MailAccount }) {
  const accountId = account.id;
  const paused = account.analysisPaused;
  const qc = useQueryClient();
  const { toast } = useToast();
  const [data, setData] = useState<{ total: number; analyzed: number } | null>(
    null,
  );
  // Sliding window of (timestamp, analyzed) samples to estimate the real rate.
  const samples = useRef<Array<{ t: number; n: number }>>([]);

  const update = useUpdateMailAccount({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() });
      },
      onError: (err: Error) =>
        toast({
          title: "Échec",
          description: err.message,
          variant: "destructive",
        }),
    },
  });
  const togglePause = () =>
    update.mutate({ id: accountId, data: { analysisPaused: !paused } });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await fetch(
          `/api/mail/accounts/${accountId}/triage-progress`,
          { credentials: "include" },
        );
        if (!r.ok) return;
        const j = (await r.json()) as { total: number; analyzed: number };
        if (active) setData(j);
      } catch {
        /* ignore — best-effort indicator */
      }
    };
    load();
    const t = setInterval(load, 10_000);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [accountId]);

  // Reset samples when switching account.
  useEffect(() => {
    samples.current = [];
  }, [accountId]);

  if (!data || data.total === 0) return null;
  const pct = Math.min(100, Math.round((data.analyzed / data.total) * 100));
  const done = data.analyzed >= data.total;

  // Record a sample + compute ETA from the oldest→newest observed rate
  // (keep ~10 min of history at one sample / 10s).
  const arr = samples.current;
  if (arr.length === 0 || arr[arr.length - 1]!.n !== data.analyzed) {
    arr.push({ t: Date.now(), n: data.analyzed });
    while (arr.length > 60) arr.shift();
  }
  let eta: string | null = null;
  if (!done && arr.length >= 2) {
    const first = arr[0]!;
    const last = arr[arr.length - 1]!;
    const dt = (last.t - first.t) / 1000;
    const dn = last.n - first.n;
    if (dt > 0 && dn > 0) {
      const rate = dn / dt; // mails/s
      eta = formatBacklogEta((data.total - data.analyzed) / rate);
    }
  }

  const barColor = done
    ? "bg-emerald-500"
    : paused
      ? "bg-amber-400"
      : "bg-primary";

  return (
    <div className="space-y-1.5" data-testid={`triage-progress-${accountId}`}>
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="text-xs font-medium flex items-center gap-1.5 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="truncate">Analyse des mails</span>
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">
            {data.analyzed.toLocaleString("fr-FR")} /{" "}
            {data.total.toLocaleString("fr-FR")}
          </span>
          {!done && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={togglePause}
              disabled={update.isPending}
              data-testid={`triage-pause-${accountId}`}
            >
              {update.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : paused ? (
                <>
                  <Play className="w-3 h-3 mr-1" />
                  Reprendre
                </>
              ) : (
                <>
                  <Pause className="w-3 h-3 mr-1" />
                  Pause
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {done ? (
        <p className="text-[10px] text-muted-foreground">
          Toute la boîte est analysée. ✓
        </p>
      ) : paused ? (
        <p className="text-[10px] text-amber-700 dark:text-amber-400">
          En pause — clique sur « Reprendre » pour continuer l'analyse.
        </p>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          Analyse en arrière-plan{eta ? ` · ${eta} restantes` : "…"}.
        </p>
      )}
    </div>
  );
}

function LearnedRulesEditor({ accountId }: { accountId: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useListMailLearnedRules(accountId);
  const rules = (data ?? []) as MailLearnedRule[];

  const [patternType, setPatternType] = useState<"sender" | "keyword">("sender");
  const [pattern, setPattern] = useState("");
  const [category, setCategory] = useState<(typeof TRIAGE_CATEGORIES)[number]>("noise");

  const create = useCreateMailLearnedRule({
    mutation: {
      onSuccess: () => {
        toast({ title: "Règle ajoutée" });
        setPattern("");
        qc.invalidateQueries({
          queryKey: getListMailLearnedRulesQueryKey(accountId),
        });
      },
      onError: (err: Error) =>
        toast({ title: "Échec", description: err.message, variant: "destructive" }),
    },
  });

  const remove = useDeleteMailLearnedRule({
    mutation: {
      onSuccess: () =>
        qc.invalidateQueries({
          queryKey: getListMailLearnedRulesQueryKey(accountId),
        }),
    },
  });

  function handleAdd() {
    if (!pattern.trim() || create.isPending) return;
    create.mutate({
      id: accountId,
      data: { patternType, pattern: pattern.trim(), category },
    });
  }

  return (
    <div className="space-y-2">
      <Label className="text-xs">Règles apprises</Label>
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      ) : rules.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Aucune règle pour l&apos;instant. Tes corrections dans les mails en créeront automatiquement.
        </p>
      ) : (
        <ul className="space-y-1">
          {rules.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded-md bg-muted/40 min-w-0 overflow-hidden"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[10px] uppercase text-muted-foreground shrink-0">
                  {r.patternType === "sender" ? "Expéditeur" : "Mot-clé"}
                </span>
                <span className="truncate font-mono">{r.pattern}</span>
                <span className="text-muted-foreground">→</span>
                <TriageBadge category={r.category} short />
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate({ accountId, ruleId: r.id })}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Row 1: type + pattern input */}
      <div className="flex items-center gap-2">
        <Select value={patternType} onValueChange={(v) => setPatternType(v as "sender" | "keyword")}>
          <SelectTrigger className="h-9 w-28 text-xs shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sender">Expéditeur</SelectItem>
            <SelectItem value="keyword">Mot-clé</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={pattern}
          onChange={(e) => setPattern(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder={
            patternType === "sender" ? "@example.com ou newsletter@" : "facture, livraison, …"
          }
          className="h-9 flex-1 text-xs"
          disabled={create.isPending}
        />
      </div>

      {/* Row 2: category + submit */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          <Select value={category} onValueChange={(v) => setCategory(v as typeof category)}>
            <SelectTrigger className="h-9 w-full text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRIAGE_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {triageLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          className="h-9 shrink-0 px-4"
          onClick={handleAdd}
          disabled={!pattern.trim() || create.isPending}
        >
          {create.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            "Ajouter"
          )}
        </Button>
      </div>
    </div>
  );
}

function OldThreadCandidatesDialog({
  accountId,
  open,
  onOpenChange,
}: {
  accountId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useListMailOldThreadCandidates(accountId, {
    query: {
      enabled: open,
      queryKey: getListMailOldThreadCandidatesQueryKey(accountId),
    },
  });
  const candidates = (data ?? []) as MailMessage[];

  const [promoteFor, setPromoteFor] = useState<string | null>(null);
  const [projectName, setProjectName] = useState("");

  function invalidateAll() {
    qc.invalidateQueries({
      queryKey: getListMailOldThreadCandidatesQueryKey(accountId),
    });
    qc.invalidateQueries({
      queryKey: getGetMailOldThreadCandidatesSummaryQueryKey(),
    });
  }

  const resolve = useResolveMailOldThreadCandidates({
    mutation: {
      onSuccess: (_data, vars) => {
        toast({
          title:
            vars.data.resolution === "kept"
              ? "Conservé(s) dans la liste"
              : "Ignoré(s)",
        });
        invalidateAll();
      },
      onError: (err: Error) =>
        toast({ title: "Échec", description: err.message, variant: "destructive" }),
    },
  });

  const promote = usePromoteMailMessageToProject({
    mutation: {
      onSuccess: () => {
        toast({ title: "Projet créé" });
        setPromoteFor(null);
        setProjectName("");
        invalidateAll();
        qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      },
      onError: (err: Error) =>
        toast({
          title: "Création impossible",
          description: err.message,
          variant: "destructive",
        }),
    },
  });

  function resolveOne(messageId: string, resolution: "kept" | "ignored") {
    resolve.mutate({
      id: accountId,
      data: { messageIds: [messageId], resolution },
    });
  }

  function ignoreAll() {
    if (candidates.length === 0) return;
    resolve.mutate({
      id: accountId,
      data: {
        messageIds: candidates.map((c) => c.id),
        resolution: "ignored",
      },
    });
  }

  function startPromote(m: MailMessage) {
    setPromoteFor(m.id);
    setProjectName(m.subject?.slice(0, 80) ?? "");
  }

  function confirmPromote(messageId: string) {
    const name = projectName.trim();
    if (!name) {
      toast({
        title: "Donne un nom au projet",
        variant: "destructive",
      });
      return;
    }
    promote.mutate({
      id: messageId,
      data: { name },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Anciens fils à revoir</DialogTitle>
          <DialogDescription>
            Mails plus vieux que ta fenêtre, qui pourraient cacher une action ou un projet.
            Garde ceux qui méritent d&apos;être traités, ignore les autres.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            Rien à rattraper, ta boîte est à jour.
          </p>
        ) : (
          <ul className="max-h-[420px] overflow-auto space-y-2 pr-1">
            {candidates.map((m) => {
              const promoting = promoteFor === m.id;
              return (
                <li
                  key={m.id}
                  className="p-3 rounded-lg border border-border space-y-2"
                  data-testid={`old-thread-row-${m.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium truncate">
                          {m.fromName || m.fromAddress}
                        </span>
                        <TriageBadge category={m.triageCategory} short />
                        {m.receivedAt && (
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {format(new Date(m.receivedAt), "d MMM yyyy", { locale: fr })}
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-medium text-foreground/90 truncate">
                        {m.subject || "(sans objet)"}
                      </p>
                      {m.triageSummary && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {m.triageSummary}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 shrink-0 w-32">
                      <Link
                        href={`/app/mail?id=${m.id}`}
                        onClick={() => onOpenChange(false)}
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          className="w-full text-xs"
                          data-testid={`button-view-mail-${m.id}`}
                        >
                          Voir le mail
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startPromote(m)}
                        disabled={resolve.isPending || promote.isPending}
                        data-testid={`button-still-active-${m.id}`}
                      >
                        Encore actif
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resolveOne(m.id, "kept")}
                        disabled={resolve.isPending}
                        data-testid={`button-keep-${m.id}`}
                      >
                        Garder
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => resolveOne(m.id, "ignored")}
                        disabled={resolve.isPending}
                        data-testid={`button-done-${m.id}`}
                      >
                        Terminé
                      </Button>
                    </div>
                  </div>
                  {promoting && (
                    <div className="flex items-center gap-2 pt-1 border-t border-border/60">
                      <Input
                        autoFocus
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="Nom du nouveau projet"
                        className="h-8 text-sm"
                        data-testid={`input-project-name-${m.id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => confirmPromote(m.id)}
                        disabled={promote.isPending}
                        data-testid={`button-confirm-promote-${m.id}`}
                      >
                        Créer
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setPromoteFor(null);
                          setProjectName("");
                        }}
                        disabled={promote.isPending}
                      >
                        Annuler
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={ignoreAll}
            disabled={candidates.length === 0 || resolve.isPending}
            className="text-muted-foreground"
          >
            Tout ignorer
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
